import type { SiteSettingsRecord } from '../types';
import { getSiteAuthRuntime, getStoredToken } from './bliss-app-auth';

function getErrorMessage(payload: unknown, fallback: string): string {
  if (!payload || typeof payload !== 'object') {
    return fallback;
  }

  const source = payload as {
    error?: string | { message?: string };
    message?: string;
    data?: { message?: string };
  };

  if (typeof source.error === 'string' && source.error) {
    return source.error;
  }

  if (source.error && typeof source.error === 'object' && typeof source.error.message === 'string') {
    return source.error.message;
  }

  if (typeof source.message === 'string' && source.message) {
    return source.message;
  }

  if (source.data && typeof source.data.message === 'string' && source.data.message) {
    return source.data.message;
  }

  return fallback;
}

async function readResponseJson(response: Response): Promise<unknown> {
  return response.json().catch(() => null);
}

async function requestSiteSettingsApi<T>(path: string, options: RequestInit = {}): Promise<T> {
  const runtime = await getSiteAuthRuntime();
  const token = getStoredToken();
  const headers = new Headers(options.headers);
  const method = (options.method || 'GET').toUpperCase();
  const hasBody = options.body != null;

  if (!token) {
    throw new Error('You need to sign in again before changing notification settings.');
  }

  if (hasBody && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (!headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(`${runtime.apiBaseUrl}${path}`, {
    ...options,
    method,
    headers,
  });

  const payload = await readResponseJson(response);
  if (!response.ok) {
    throw new Error(getErrorMessage(payload, 'The site settings workspace could not reach the Bliss app API.'));
  }

  const source = payload as { data?: T };
  return (source.data ?? payload) as T;
}

export async function getSiteSettings(): Promise<SiteSettingsRecord | null> {
  return requestSiteSettingsApi<SiteSettingsRecord | null>('/settings/site');
}

export async function updateSiteSettings(input: {
  site_name: string;
  contact_email: string | null;
  contact_phone?: string | null;
  site_description?: string | null;
}): Promise<SiteSettingsRecord | null> {
  return requestSiteSettingsApi<SiteSettingsRecord | null>('/settings/site', {
    method: 'PUT',
    body: JSON.stringify({
      site_name: input.site_name,
      contact_email: input.contact_email,
      contact_phone: input.contact_phone ?? null,
      site_description: input.site_description ?? null,
    }),
  });
}
